import { Table, TableBody, TableCell, TableHead, TablePagination, TableRow } from '@mui/material';
import { FC, useMemo, useState } from 'react';
import { BulkUploadType } from 'api/Responses/BulkUploadResponses';
import { useLanguage } from 'context/AppContext';
import DataUploadListItem from './DataUploadListItem';
import * as Styles from './DataUploadListTable.styles';

interface DataUploadListTableParams {
  uploadedFiles: BulkUploadType[];
}

const DataUploadListTable: FC<DataUploadListTableParams> = ({ uploadedFiles }) => {
  const [language] = useLanguage();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows = useMemo(
    () => (page > 0 ? Math.max(0, (1 + page) * rowsPerPage - uploadedFiles.length) : 0),
    [page, rowsPerPage, uploadedFiles.length],
  );

  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Styles.Table>
      <Styles.Container>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <Styles.HeadTableCell>{language.DataUpload.table.status}</Styles.HeadTableCell>
              <Styles.HeadTableCell>{language.DataUpload.table.fileName}</Styles.HeadTableCell>
              <Styles.HeadTableCell>{language.DataUpload.table.uploadType}</Styles.HeadTableCell>
              <Styles.HeadTableCell>{language.DataUpload.table.uploader}</Styles.HeadTableCell>
              <Styles.HeadTableCell>{language.DataUpload.table.dateUploaded}</Styles.HeadTableCell>
              <Styles.HeadTableCell>{language.DataUpload.table.totalUploadedItems}</Styles.HeadTableCell>
              <Styles.HeadTableCell>{language.DataUpload.table.download}</Styles.HeadTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(rowsPerPage > 0
              ? uploadedFiles.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              : uploadedFiles
            ).map(file => (
              <DataUploadListItem key={file.upload_id} file={file} />
            ))}
            {emptyRows > 0 ? (
              <TableRow style={{ height: 53 * emptyRows }}>
                <TableCell colSpan={6} />
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </Styles.Container>
      <Styles.Footer>
        <TableRow>
          <TablePagination
            rowsPerPageOptions={[10, 25, { label: 'All', value: -1 }]}
            count={uploadedFiles.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableRow>
      </Styles.Footer>
    </Styles.Table>
  );
};

export default DataUploadListTable;
